import { notifyError } from "@/lib/notify-error";
import { useEffect, useState } from "react";
import { useMutation } from "convex/react";
import { CalendarClock } from "lucide-react";

import { api } from "@constructionautomation/backend";
import type { Id } from "@constructionautomation/backend/convex/_generated/dataModel";
import { Button } from "@/components/ui/button";
import { DatePicker } from "@/components/ui/date-picker";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  formatDurationDays,
  parseDateKeyToMs,
} from "@/features/field-pilot/schedule-format";
import { getUserFacingErrorMessage } from "@/lib/user-facing-errors";

const MS_PER_DAY = 24 * 60 * 60 * 1000;

function durationDaysFromRange(
  startDateKey: string | null,
  finishDateKey: string | null
): number | null {
  const startMs = parseDateKeyToMs(startDateKey);
  const finishMs = parseDateKeyToMs(finishDateKey);
  if (startMs === null || finishMs === null || finishMs < startMs) return null;
  return Math.round((finishMs - startMs) / MS_PER_DAY);
}

export interface FieldPilotTagScheduleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onOpenFullSchedule: () => void;
  tagId: Id<"fieldPilotTags">;
  rawValue: string;
  displayName: string;
  startDateKey: string | null;
  finishDateKey: string | null;
}

export function FieldPilotTagScheduleDialog({
  open,
  onOpenChange,
  onOpenFullSchedule,
  tagId,
  rawValue,
  displayName,
  startDateKey,
  finishDateKey,
}: FieldPilotTagScheduleDialogProps) {
  const setDisplayName = useMutation(api.fieldPilot.tags.setDisplayName);
  const setSchedule = useMutation(api.fieldPilot.tags.setSchedule);

  const [localDisplayName, setLocalDisplayName] = useState(displayName);

  useEffect(() => {
    if (!open) return;
    setLocalDisplayName(displayName);
  }, [open, displayName]);

  const durationDays = durationDaysFromRange(startDateKey, finishDateKey);
  const taskLabel = displayName.trim() || rawValue;

  const handleDisplayNameSave = () => {
    const trimmed = localDisplayName.trim();
    if (trimmed === displayName) return;
    setDisplayName({ tagId, displayName: trimmed }).catch((error) =>
      notifyError(error, getUserFacingErrorMessage(error, "Failed to update name"))
    );
  };

  const handleScheduleChange = (args: {
    startDateKey?: string | null;
    finishDateKey?: string | null;
  }) => {
    setSchedule({ tagId, ...args }).catch((error) =>
      notifyError(error, getUserFacingErrorMessage(error, "Failed to update dates"))
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Edit schedule</DialogTitle>
          <DialogDescription>
            Set start and finish dates for {taskLabel}. Progress tracks automatically from item
            statuses.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="tag-schedule-name">Task name</Label>
            <Input
              id="tag-schedule-name"
              value={localDisplayName}
              onChange={(event) => setLocalDisplayName(event.target.value)}
              onBlur={handleDisplayNameSave}
              placeholder={rawValue}
              aria-label={`Task name for ${rawValue}`}
            />
            {displayName !== rawValue ? (
              <p className="text-xs text-muted-foreground">Linked tag: {rawValue}</p>
            ) : null}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="min-w-0 space-y-1.5">
              <Label>Start date</Label>
              <DatePicker
                className="w-full"
                size="sm"
                value={startDateKey ?? ""}
                max={finishDateKey ?? undefined}
                onChange={(value) => handleScheduleChange({ startDateKey: value || null })}
                aria-label={`Start date for ${rawValue}`}
              />
            </div>
            <div className="min-w-0 space-y-1.5">
              <Label>Finish date</Label>
              <DatePicker
                className="w-full"
                size="sm"
                value={finishDateKey ?? ""}
                min={startDateKey ?? undefined}
                onChange={(value) => handleScheduleChange({ finishDateKey: value || null })}
                aria-label={`Finish date for ${rawValue}`}
              />
            </div>
          </div>

          <p className="text-sm text-muted-foreground">
            Duration: {formatDurationDays(durationDays)}
          </p>
        </div>

        <DialogFooter className="sm:justify-between">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="gap-2"
            onClick={onOpenFullSchedule}
          >
            <CalendarClock className="size-4" />
            Edit full schedule
          </Button>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
